import { CreditCard, LockKeyhole, MapPin, RefreshCw, Wallet } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { adminApi, storefrontApi } from '../../lib/api'
import type { EfiAdminSettings } from '../../types/commerce'

type EfiForm = {
  enabled: boolean
  sandbox: boolean
  credit_enabled: boolean
  pix_enabled: boolean
  client_id: string
  client_secret: string
}
type LiveStatus = Awaited<ReturnType<typeof adminApi.testEfiConnection>>

const empty: EfiForm = {
  enabled: false, sandbox: true, credit_enabled: true, pix_enabled: false, client_id: '', client_secret: ''
}
const money = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export function AdminPayments() {
  const [form, setForm] = useState<EfiForm>(empty)
  const [saved, setSaved] = useState<EfiAdminSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [checking, setChecking] = useState(false)
  const [live, setLive] = useState<LiveStatus | null>(null)
  const [liveError, setLiveError] = useState('')
  const [ackProduction, setAckProduction] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [editingCredentials, setEditingCredentials] = useState(false)
  const [whatsapp, setWhatsApp] = useState('')
  const [whatsappLoaded, setWhatsAppLoaded] = useState(false)
  const [savingWhatsApp, setSavingWhatsApp] = useState(false)
  const [whatsappNotice, setWhatsAppNotice] = useState('')

  const credentialsAlreadySaved = Boolean(saved?.client_id_configured && saved?.client_secret_configured)
  const showCredentialFields = !credentialsAlreadySaved || editingCredentials
  const settingsChanged = Boolean(saved && (
    form.enabled !== saved.enabled ||
    form.sandbox !== saved.sandbox ||
    form.credit_enabled !== saved.credit_enabled ||
    form.pix_enabled !== saved.pix_enabled
  ))
  const credentialChanges = showCredentialFields && Boolean(form.client_id.trim() || form.client_secret.trim())
  const hasUnsavedChanges = settingsChanged || credentialChanges

  const set = <K extends keyof EfiForm>(key: K, value: EfiForm[K]) =>
    setForm(previous => ({ ...previous, [key]: value }))

  useEffect(() => {
    let mounted = true
    adminApi.getEfiSettings()
      .then(s => {
        if (!mounted) return
        setSaved(s)
        setForm({
          enabled: s.enabled, sandbox: s.sandbox,
          credit_enabled: s.credit_enabled, pix_enabled: s.pix_enabled,
          client_id: '', client_secret: ''
        })
      })
      .catch(err => { if (mounted) setError(err instanceof Error ? err.message : 'Não foi possível carregar as configurações.') })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    storefrontApi.checkoutSettings()
      .then(settings => setWhatsApp(settings.whatsapp ?? ''))
      .catch(err => setError(err instanceof Error ? err.message : 'Não foi possível consultar o WhatsApp.'))
      .finally(() => setWhatsAppLoaded(true))
  }, [])

  const saveWhatsApp = async () => {
    setError(''); setWhatsAppNotice('')
    setSavingWhatsApp(true)
    try {
      const number = await adminApi.saveStoreWhatsApp(whatsapp)
      setWhatsApp(number ?? '')
      setWhatsAppNotice(number
        ? 'WhatsApp salvo. O link para cotar entregas por motoboy está configurado.'
        : 'WhatsApp removido. A cotação por motoboy ficará indisponível.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar o WhatsApp.')
    } finally {
      setSavingWhatsApp(false)
    }
  }

  // Sempre que o painel é aberto, e a cada 5 minutos enquanto estiver aberto,
  // consultamos novamente a conta Efí. Não existe campo de taxa para digitar.
  useEffect(() => {
    if (!saved?.client_id_configured || !saved.client_secret_configured) return
    let mounted = true
    const refresh = async () => {
      setChecking(true)
      setLiveError('')
      try {
        const response = await adminApi.testEfiConnection()
        if (mounted) setLive(response)
      } catch (err) {
        if (mounted) {
          setLive(null)
          setLiveError(err instanceof Error ? err.message : 'A consulta à Efí falhou.')
        }
      } finally { if (mounted) setChecking(false) }
    }
    void refresh()
    const timer = window.setInterval(() => void refresh(), 5 * 60 * 1000)
    return () => { mounted = false; window.clearInterval(timer) }
  }, [saved?.client_id_configured, saved?.client_secret_configured, saved?.sandbox])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(''); setNotice('')
    if (!saved) {
      setError('Não foi possível carregar a configuração. Reabra esta página antes de alterar os pagamentos.')
      return
    }
    if (!hasUnsavedChanges) {
      setNotice('As configurações já estão salvas. Para consultar a Efí, use o botão da seção Parcelamento automático.')
      return
    }
    if (form.enabled && !form.sandbox && !ackProduction) {
      setError('Confirme a ativação em produção somente após testar o pagamento.')
      return
    }
    setSaving(true)
    try {
      const result = await adminApi.saveEfiSettings({
        enabled: form.enabled, sandbox: form.sandbox,
        credit_enabled: form.credit_enabled, pix_enabled: form.pix_enabled,
        client_id: showCredentialFields ? form.client_id.trim() : '',
        client_secret: showCredentialFields ? form.client_secret.trim() : ''
      })
      setSaved(result)
      setForm(previous => ({ ...previous, client_id: '', client_secret: '' }))
      setEditingCredentials(false)
      setNotice('Configurações salvas. Não é necessário salvar novamente para consultar as parcelas.')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível salvar a configuração.'
      setError(/Load failed|Failed to fetch|NetworkError|fetch failed/i.test(message)
        ? 'Falha de comunicação com o Supabase. Não digite nem reenvie suas credenciais. Recarregue o painel para conferir o que foi salvo.'
        : message)
    } finally { setSaving(false) }
  }

  if (loading) return <div className="admin-page"><div className="admin-loading">Carregando pagamentos...</div></div>

  return <div className="admin-page">
    <div className="admin-page__heading">
      <div>
        <span>CONFIGURAÇÕES</span>
        <h1>Pagamentos Efí Bank</h1>
        <p>Gerencie as credenciais e os métodos de pagamento. Juros e parcelas são consultados diretamente na Efí, sem campos de taxas manuais.</p>
      </div>
    </div>

    {error && <div className="form-error" role="alert">{error}</div>}
    {notice && <div className="form-success" role="status">{notice}</div>}

    <form className="admin-form" onSubmit={submit}>
      <section className="admin-panel admin-form__section">
        <h2><LockKeyhole size={19} /> Conexão com a Efí</h2>
        <p>O Client ID e o Client Secret são armazenados de forma protegida no Supabase Vault. A loja não mostra essas credenciais novamente.</p>
        {credentialsAlreadySaved && <p className="server-note" role="status">
          <strong>Credenciais já cadastradas.</strong> Você não precisa digitar nem salvar novamente para consultar o crédito.
          A autenticação e as condições das parcelas são verificadas separadamente abaixo.
        </p>}
        {showCredentialFields ? <div>
          <div className="form-grid">
            <label>Client ID {saved?.client_id_configured && <small>(já cadastrado)</small>}
              <input type="password" autoComplete="new-password" value={form.client_id}
                placeholder={saved?.client_id_configured ? 'Deixe vazio para manter' : 'Cole o Client ID'}
                onChange={e => set('client_id', e.target.value)} />
            </label>
            <label>Client Secret {saved?.client_secret_configured && <small>(já cadastrado)</small>}
              <input type="password" autoComplete="new-password" value={form.client_secret}
                placeholder={saved?.client_secret_configured ? 'Deixe vazio para manter' : 'Cole o Client Secret'}
                onChange={e => set('client_secret', e.target.value)} />
            </label>
          </div>
          {credentialsAlreadySaved && <button type="button" className="button button--outline"
            onClick={() => {
              setEditingCredentials(false)
              setForm(previous => ({ ...previous, client_id: '', client_secret: '' }))
            }}>CANCELAR ALTERAÇÃO DE CREDENCIAIS</button>}
        </div> : <button type="button" className="button button--outline"
          onClick={() => {
            setEditingCredentials(true)
            setError('')
            setForm(previous => ({ ...previous, client_id: '', client_secret: '' }))
          }}>ALTERAR CLIENT ID OU CLIENT SECRET</button>}
        <div className="form-grid">
          <label>Ambiente
            <select value={form.sandbox ? 'sandbox' : 'production'} onChange={e => {
              set('sandbox', e.target.value === 'sandbox'); setAckProduction(false)
            }}>
              <option value="sandbox">Homologação (testes)</option>
              <option value="production">Produção (pagamentos reais)</option>
            </select>
          </label>
          <label className="gk-check-field">
            <input type="checkbox" checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />
            Habilitar pagamentos na GK Maison
          </label>
        </div>
        {!form.sandbox && form.enabled && <label className="gk-check-field">
          <input type="checkbox" checked={ackProduction} onChange={e => setAckProduction(e.target.checked)} />
          Confirmei que o pagamento foi testado antes de habilitar a produção
        </label>}
      </section>

      <section className="admin-panel admin-form__section">
        <h2><CreditCard size={19} /> Parcelamento automático pela Efí</h2>
        <label className="gk-check-field">
          <input type="checkbox" checked={form.credit_enabled} onChange={e => set('credit_enabled', e.target.checked)} />
          Permitir cartão de crédito
        </label>
        <p>O checkout consulta as opções de 1 a 12 parcelas de acordo com a bandeira do cartão e o valor da compra. Se a Efí mudar os <strong>juros das parcelas</strong>, a próxima consulta refletirá os novos valores.</p>
        <p className="server-note"><strong>Consulta automática de exemplo:</strong> Visa, compra de {money(100)}. Atualiza ao abrir o painel e a cada cinco minutos, sem você preencher tarifas.</p>

        <button type="button" className="button button--outline" disabled={checking || !saved?.client_id_configured || !saved?.client_secret_configured}
          onClick={async () => {
            setChecking(true); setLiveError('')
            try {
              setLive(await adminApi.testEfiConnection())
            } catch (err) {
              setLive(null)
              setLiveError(err instanceof Error ? err.message : 'Não foi possível verificar o banco.')
            } finally { setChecking(false) }
          }}>CONSULTAR A EFÍ AGORA (SEM SALVAR)</button>
        {checking && <p className="server-note" role="status"><RefreshCw size={14} /> Atualizando informações com a Efí...</p>}
        {liveError && <div className="form-error" role="status">{liveError}</div>}
        {live?.ok && <div className="form-success" role="status">
          Conexão com a Efí autenticada com sucesso ({live.environment}).
          Consulta realizada em {new Date(live.consulted_at).toLocaleString('pt-BR')}.
        </div>}
        {live?.installments_warning && <p className="server-note" role="status">
          A conta Efí foi autenticada, mas as condições do parcelamento não foram retornadas: {live.installments_warning}
          Isso não significa que o Client ID ou o Client Secret estejam inválidos.
        </p>}
        {live?.installments && <div>
          <p className="server-note">Consulta realizada em {new Date(live.consulted_at).toLocaleString('pt-BR')} ({live.environment}).</p>
          <table className="gk-fee-table">
            <thead><tr><th>Parcelas informadas pela Efí</th><th>Valor da parcela</th><th>Total</th></tr></thead>
            <tbody>{live.installments.map(option => <tr key={option.number}>
              <td>{option.number}x {option.has_interest ? '(com juros)' : '(sem juros)'}</td>
              <td>{money(option.monthly_amount)}</td>
              <td>{money(option.total)}</td>
            </tr>)}</tbody>
          </table>
        </div>}

        <p className="server-note"><strong>Importante:</strong> esses valores são juros e parcelas cobrados do comprador. A Efí não documenta nessa consulta a tarifa de processamento que desconta da GK Maison. Por isso, o painel não apresenta porcentagens inventadas nem afirma que essas tarifas foram repassadas automaticamente. Com o link de pagamento atual, o cliente confirma as parcelas na página da Efí.</p>
        <a className="text-link" href="https://dev.efipay.com.br/docs/api-cobrancas/cartao/" target="_blank" rel="noopener noreferrer">Como a Efí calcula as parcelas na API</a>
      </section>

      <section className="admin-panel admin-form__section">
        <h2><Wallet size={19} /> Pix via Bolix</h2>
        <p>O pagamento via Pix com Bolix é processado na Efí; a tela de pagamento também pode oferecer boleto. O valor da operação é calculado no servidor.</p>
        <label className="gk-check-field">
          <input type="checkbox" checked={form.pix_enabled} onChange={e => set('pix_enabled', e.target.checked)} />
          Permitir Pix via Bolix
        </label>
        <p className="server-note">Tarifas de processamento do Bolix também são descontadas de acordo com o contrato Efí. O checkout não simula uma tarifa sem confirmação do provedor. Débito online continua indisponível.</p>
      </section>

      <section className="admin-panel admin-form__section">
        <h2><MapPin size={19} /> Retirada e entrega por motoboy</h2>
        <p><strong>Retirada gratuita:</strong> GK Maison, Sala 3, Rua Generoso Alves Vieira, 14, Jardim Santa Carolina, Sumaré/SP.</p>
        <p>O cliente também poderá solicitar uma entrega por motoboy. Antes de finalizar, a loja calculará o frete no WhatsApp. Nenhum valor de entrega será cobrado automaticamente sem essa cotação.</p>
        <label>WhatsApp da GK Maison para calcular o frete (com DDD)
          <input type="tel" autoComplete="tel" placeholder="Ex.: (19) 99999-9999"
            value={whatsapp} onChange={event => { setWhatsApp(event.target.value); setWhatsAppNotice('') }} />
        </label>
        {whatsappNotice && <div className="form-success" role="status">{whatsappNotice}</div>}
        <div className="panel-actions">
          <button type="button" className="button button--outline" disabled={!whatsappLoaded || savingWhatsApp} onClick={() => void saveWhatsApp()}>
            {savingWhatsApp ? 'SALVANDO WHATSAPP...' : 'SALVAR WHATSAPP PARA FRETE'}
          </button>
        </div>
        <p className="server-note">Use o número oficial que atende os clientes. Você pode alterá-lo aqui a qualquer momento.</p>
      </section>
      <div className="admin-form__actions">
        <button className="button button--dark" disabled={saving || !saved || !hasUnsavedChanges}>
          {saving ? 'SALVANDO...' : hasUnsavedChanges ? 'SALVAR ALTERAÇÕES' : 'CONFIGURAÇÕES JÁ SALVAS'}
        </button>
      </div>
    </form>
  </div>
}
